import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, CircleDot, Clock3, Copy, Film, History, Image, LayoutDashboard, ListChecks, Play, RefreshCw, ShieldCheck, Sparkles, Square, Trash2, UserRound, Wand2, X } from 'lucide-react';
import {
  deleteUgcFlowkitProduct,
  formatGoogleFlowPrompts,
  getCurrentUgcFlowkitBatch,
  getExplicitlySelectedBatchId,
  getUgcFlowkitBatch,
  resumeUgcFlowkitBatch,
  resumeUgcFlowkitProduct,
  retryInvalidUgcFlowkitPlans,
  setExplicitlySelectedBatchId,
  stopUgcFlowkitProduct,
  type UgcFlowkitBatch,
  type UgcFlowkitProduct,
  type UgcFlowkitProductStatus,
} from '../services/ugcFlowkit';
import UgcBatchesQueueView from './UgcBatchesQueueView';
import { useStore } from '../services/store';

const STAGES: Record<UgcFlowkitProductStatus, { label: string; detail: string; className: string }> = {
  queued: { label: 'Queued', detail: 'Waiting for the product planner', className: 'border-[#d6bd7c] bg-[#fdf6e9] text-[#6d5b3f]' },
  planning: { label: 'Planning', detail: 'Provisioning references, then Codex planning; Gemini only if Codex is unavailable', className: 'border-[#b54426]/35 bg-[#b54426]/10 text-[#8f3219]' },
  validated: { label: 'Plan schema validated', detail: 'Reference, prompt-pair and duration checks passed; rendered media can proceed to generation and merge', className: 'border-[#b87a14]/35 bg-[#b87a14]/10 text-[#6f4810]' },
  provisioned: { label: 'References ready', detail: 'Flowkit UUID references are provisioned', className: 'border-[#865916]/35 bg-[#865916]/10 text-[#6f4810]' },
  generating: { label: 'Generating', detail: 'Flowkit is rendering the approved scenes', className: 'border-[#b54426]/35 bg-[#b54426]/10 text-[#8f3219]' },
  merged: { label: 'Merged', detail: 'Auto-merge completed for this product', className: 'border-[#b87a14]/45 bg-[#b87a14]/15 text-[#6f4810]' },
  stopped_safe: { label: 'Stopped safely', detail: 'This product was isolated; other products can continue', className: 'border-[#dc553c]/40 bg-[#dc553c]/10 text-[#8c2d1f]' },
};
type UgcWorkspaceTab = 'references' | 'plan' | 'process' | 'rules' | 'dashboard' | 'batches';
const WORKSPACE_TABS: Array<{ id: UgcWorkspaceTab; label: string; icon: typeof Image }> = [
  { id: 'references', label: 'References', icon: Image },
  { id: 'plan', label: 'Plan', icon: ListChecks },
  { id: 'process', label: 'Process', icon: CircleDot },
  { id: 'rules', label: 'Rules', icon: ShieldCheck },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'batches', label: 'Batches / Queue', icon: History },
];

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusIcon(status: UgcFlowkitProductStatus) {
  if (status === 'merged') return <CheckCircle2 className="h-4 w-4" />;
  if (status === 'stopped_safe') return <AlertTriangle className="h-4 w-4" />;
  if (status === 'queued') return <Clock3 className="h-4 w-4" />;
  return <CircleDot className="h-4 w-4" />;
}

const PIPELINE_STEPS = ['References', 'Plan', 'Images', 'Videos', 'Merge'] as const;

function getPipelineStepState(product: UgcFlowkitProduct): { completedCount: number; activeIndex: number; failedIndex: number | null } {
  const runtime = product.runtime;
  if (product.status === 'merged' || runtime?.merged) {
    return { completedCount: PIPELINE_STEPS.length, activeIndex: -1, failedIndex: null };
  }

  let completed = 0;

  // 0: References
  const hasReferences = Boolean(runtime?.locationMediaId && (runtime?.productMediaId || runtime?.productUploadFile));
  if (hasReferences || runtime?.plan) {
    completed = 1;
  }

  // 1: Plan
  if (runtime?.plan) {
    completed = 2;
  }

  // 2: Images
  const imagesDone = Boolean(
    runtime?.videoRequestIds?.length ||
    (runtime?.phase && ['generating_videos', 'reviewing', 'reworking_review', 'merging', 'merged'].includes(runtime.phase))
  );
  if (imagesDone) {
    completed = 3;
  }

  // 3: Videos
  const videosDone = Boolean(
    runtime?.review ||
    (runtime?.phase && ['reviewing', 'reworking_review', 'merging', 'merged'].includes(runtime.phase))
  );
  if (videosDone) {
    completed = 4;
  }

  // 4: Merge
  if (runtime?.merged) {
    completed = 5;
  }

  if (product.status === 'stopped_safe') {
    return { completedCount: completed, activeIndex: -1, failedIndex: completed < PIPELINE_STEPS.length ? completed : null };
  }

  const activeIndex = completed < PIPELINE_STEPS.length ? completed : -1;
  return { completedCount: completed, activeIndex, failedIndex: null };
}

function getStageDetail(product: UgcFlowkitProduct): string {
  if (product.runtime?.phase === 'merging') return 'Merging scene clips with OpenCut (end-trim)...';
  if (product.runtime?.phase === 'reviewing' || product.runtime?.phase === 'reworking_review') return 'Legacy review checkpoint detected; Resume continues directly to merge.';
  if (product.runtime?.phase === 'generating_videos') return 'Generating 8-second scene videos in Flowkit...';
  if (product.runtime?.phase === 'generating_images') return 'Generating frame-zero scene images in Flowkit...';
  if (product.runtime?.phase === 'creating_scenes') return 'Creating Flowkit scene entities...';
  if (product.runtime?.phase === 'planning') return product.runtime.productVision?.provider === 'gemini-fallback'
    ? 'Product images inspected by Gemini fallback; planning scene beats...'
    : 'Planning scene beats with Codex/Gemini...';
  if (product.runtime?.phase === 'provisioning') return 'Provisioning Flowkit UUID references...';
  return STAGES[product.status]?.detail ?? 'Processing product...';
}
function idShort(id?: string) { return id ? `${id.slice(0, 8)}…${id.slice(-4)}` : 'Not created'; }
function ReferenceLine({ label, id }: { label: string; id?: string }) {
  return <div className="flex items-center justify-between gap-3 rounded border border-[#d6bd7c]/50 bg-white/45 px-2.5 py-2"><span className="font-bold text-[#2f2414]">{label}</span><code className="truncate text-[11px] text-[#6d5b3f]" title={id}>{idShort(id)}</code></div>;
}

export default function UgcFlowkitPipelinePanel() {
  const { ugcFlowkitBatchId, setUgcFlowkitBatchId, setActiveTab } = useStore();
  const [batch, setBatch] = useState<UgcFlowkitBatch | null>(null);
  const [loading, setLoading] = useState(Boolean(ugcFlowkitBatchId));
  const [discovering, setDiscovering] = useState(!ugcFlowkitBatchId);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [workspaceTab, setWorkspaceTab] = useState<UgcWorkspaceTab>('process');
  const [resuming, setResuming] = useState(false);
  const [retryingPlans, setRetryingPlans] = useState(false);
  const [productBusy, setProductBusy] = useState<Record<string, 'stopping' | 'resuming' | 'deleting'>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const pausedRef = useRef(false);


  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const refreshingRef = useRef(false);
  pausedRef.current = Boolean(batch?.runtime?.blocked);

  const refresh = useCallback(async () => {
    if (!ugcFlowkitBatchId || refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      const next = await getUgcFlowkitBatch(ugcFlowkitBatchId);
      setBatch(next);
      setError('');
      setLastUpdated(new Date());
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Unable to load the UGC Flowkit batch.';
      // Stale or deleted batch ID in localStorage — clear it and attempt to discover the latest active batch
      if (/404|not found|ENOENT/i.test(message)) {
        setBatch(null);
        setError('Batch đã bị xoá hoặc không tồn tại. Đang tìm batch khả dụng gần nhất...');
        void getCurrentUgcFlowkitBatch().then(current => {
          if (current) {
            setExplicitlySelectedBatchId(current.id);
            setUgcFlowkitBatchId(current.id);
            setBatch(current);
            setError('');
          } else {
            setExplicitlySelectedBatchId(null);
            setUgcFlowkitBatchId('');
            setWorkspaceTab('batches');
            setError('');
          }
        }).catch(() => {
          setExplicitlySelectedBatchId(null);
          setUgcFlowkitBatchId('');
          setWorkspaceTab('batches');
        });
        return;
      }
      // A status request launched before the batch paused may time out later.
      // The persisted pause, not that stale browser timeout, is authoritative.
      if (!pausedRef.current) setError(message);
    } finally {
      refreshingRef.current = false;
      setLoading(false);
    }
  }, [setUgcFlowkitBatchId, ugcFlowkitBatchId]);

  useEffect(() => {
    if (ugcFlowkitBatchId && getExplicitlySelectedBatchId() === ugcFlowkitBatchId) {
      setDiscovering(false);
      return;
    }
    let active = true;
    void getCurrentUgcFlowkitBatch()
      .then(current => {
        if (active && current) {
          setExplicitlySelectedBatchId(current.id);
          if (current.id !== ugcFlowkitBatchId) {
            setUgcFlowkitBatchId(current.id);
          }
          setBatch(current);
          setError('');
        }
      })
      .catch(reason => {
        if (active && !ugcFlowkitBatchId) {
          setError(reason instanceof Error ? reason.message : 'Unable to find the current UGC Flowkit batch.');
        }
      })
      .finally(() => { if (active) setDiscovering(false); });
    return () => { active = false; };
  }, [setUgcFlowkitBatchId, ugcFlowkitBatchId]);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!batch || batch.runtime?.blocked || batch.products.every(product => product.status === 'merged' || product.status === 'stopped_safe')) return;
    const interval = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(interval);
  }, [batch, refresh]);

  const resumeAfterBridgeRepair = async () => {
    if (!ugcFlowkitBatchId) return;
    setResuming(true);
    setError('');
    try {
      await resumeUgcFlowkitBatch(ugcFlowkitBatchId);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to resume the UGC Flowkit batch.');
    } finally {
      setResuming(false);
    }
  };
  const retryInvalidPlans = async () => {
    if (!ugcFlowkitBatchId) return;
    setRetryingPlans(true); setError('');
    try {
      await retryInvalidUgcFlowkitPlans(ugcFlowkitBatchId);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to retry invalid plans.');
    } finally { setRetryingPlans(false); }
  };

  const handleStopProduct = async (productId: string) => {
    if (!ugcFlowkitBatchId || productBusy[productId]) return;
    setProductBusy(prev => ({ ...prev, [productId]: 'stopping' }));
    setError('');
    try {
      await stopUgcFlowkitProduct(ugcFlowkitBatchId, productId);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to stop this product pipeline.');
    } finally {
      setProductBusy(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const handleResumeProduct = async (productId: string) => {
    if (!ugcFlowkitBatchId || productBusy[productId]) return;
    setProductBusy(prev => ({ ...prev, [productId]: 'resuming' }));
    setError('');
    try {
      await resumeUgcFlowkitProduct(ugcFlowkitBatchId, productId);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to resume this product pipeline.');
    } finally {
      setProductBusy(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const handleDeleteProduct = async (productId: string, productName: string) => {
    if (!ugcFlowkitBatchId || productBusy[productId]) return;
    if (!window.confirm(`Delete the pipeline for "${productName}" from this batch?`)) return;
    setProductBusy(prev => ({ ...prev, [productId]: 'deleting' }));
    setError('');
    try {
      await deleteUgcFlowkitProduct(ugcFlowkitBatchId, productId);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to delete this product pipeline.');
    } finally {
      setProductBusy(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const renderBatchesTab = () => (
    <UgcBatchesQueueView
      selectedBatchId={ugcFlowkitBatchId}
      onSelectBatch={(batchId) => {
        setExplicitlySelectedBatchId(batchId);
        setUgcFlowkitBatchId(batchId);
        setWorkspaceTab('process');
      }}
      onActionComplete={() => {
        void refresh();
      }}
    />
  );

  if (!ugcFlowkitBatchId && discovering) {
    return <div className="h-full overflow-y-auto bg-[#fdf6e9] p-6"><div className="mx-auto mt-12 max-w-xl rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-6 text-center text-sm text-[#6d5b3f]">Finding your current UGC Flowkit batch…</div></div>;
  }

  if (!ugcFlowkitBatchId && workspaceTab !== 'batches') {
    return <div className="h-full overflow-y-auto bg-[#fdf6e9] p-3 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <nav aria-label="UGC Flowkit workspace" className="flex overflow-x-auto sm:flex-wrap gap-2 rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-2 shadow-sm">
          {WORKSPACE_TABS.map(tab => {
            const Icon = tab.icon;
            const selected = tab.id === workspaceTab;
            return <button key={tab.id} type="button" onClick={() => setWorkspaceTab(tab.id)} className={`inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-lg px-3 py-2 text-xs font-black ${selected ? 'bg-[#b54426] text-white' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'}`}><Icon className="h-3.5 w-3.5" />{tab.label}</button>;
          })}
        </nav>
        <div className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-8 text-center shadow-sm">
          <Film className="mx-auto h-8 w-8 text-[#865916]" />
          <h2 className="mt-3 text-base font-black text-[#2f2414]">Chưa chọn batch UGC Flowkit nào</h2>
          <p className="mt-2 text-sm text-[#6d5b3f]">Bắt đầu batch mới từ tab Master Prompt, hoặc chọn xem lại lịch sử các batch trong tab "Batches / Queue".</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button onClick={() => setWorkspaceTab('batches')} className="rounded border border-[#d6bd7c] bg-white px-4 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50">Xem lịch sử & hàng đợi</button>
            <button onClick={() => setActiveTab('masterPrompt')} className="inline-flex items-center gap-1.5 rounded-lg bg-[#b54426] px-5 py-2 text-xs font-bold text-[#fff8ec] hover:bg-[#8f3219] shadow transition-colors"><Play className="h-3.5 w-3.5" />Bắt đầu batch mới (Master Prompt)</button>
          </div>
        </div>
      </div>
    </div>;
  }

  if (!ugcFlowkitBatchId && workspaceTab === 'batches') {
    return <div className="h-full overflow-y-auto bg-[#fdf6e9] p-3 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <nav aria-label="UGC Flowkit workspace" className="flex overflow-x-auto sm:flex-wrap gap-2 rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-2 shadow-sm">
          {WORKSPACE_TABS.map(tab => {
            const Icon = tab.icon;
            const selected = tab.id === workspaceTab;
            return <button key={tab.id} type="button" onClick={() => setWorkspaceTab(tab.id)} className={`inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-lg px-3 py-2 text-xs font-black ${selected ? 'bg-[#b54426] text-white' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'}`}><Icon className="h-3.5 w-3.5" />{tab.label}</button>;
          })}
        </nav>
        {renderBatchesTab()}
      </div>
    </div>;
  }

  const counts = batch?.products.reduce<Record<UgcFlowkitProductStatus, number>>((result, product) => {
    result[product.status]++;
    return result;
  }, { queued: 0, planning: 0, validated: 0, provisioned: 0, generating: 0, merged: 0, stopped_safe: 0 });

  return <div className="h-full overflow-y-auto bg-[#fdf6e9] p-3 sm:p-6">
    <div className="mx-auto max-w-6xl space-y-4">
      <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#2f2414]"><Sparkles className="h-5 w-5 text-[#b54426]" /><h2 className="text-base font-black">Current UGC Flowkit pipeline</h2></div>
            <p className="mt-1 text-xs text-[#6d5b3f]">Batch {ugcFlowkitBatchId}{batch ? ` · started ${formatTime(batch.createdAt)}` : ''}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setActiveTab('masterPrompt')} className="inline-flex items-center gap-1.5 rounded-lg bg-[#b54426] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#8f3219] shadow-sm transition-colors"><Play className="h-3.5 w-3.5" />Tạo batch mới</button>
            <button onClick={() => void refresh()} disabled={loading} className="inline-flex items-center gap-2 rounded border border-[#d6bd7c] px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50 disabled:opacity-60"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
          </div>
        </div>
        {batch && <>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded border border-[#d6bd7c]/70 bg-[#fdf6e9] p-3"><p className="text-[10px] font-bold uppercase tracking-wide text-[#6d5b3f]">Products</p><p className="mt-1 text-xl font-black text-[#2f2414]">{batch.products.length}</p></div>
            <div className="rounded border border-[#b54426]/25 bg-[#b54426]/5 p-3"><p className="text-[10px] font-bold uppercase tracking-wide text-[#8f3219]">Active</p><p className="mt-1 text-xl font-black text-[#8f3219]">{counts ? counts.planning + counts.validated + counts.provisioned + counts.generating : 0}</p></div>
            <div className="rounded border border-[#b87a14]/35 bg-[#b87a14]/10 p-3"><p className="text-[10px] font-bold uppercase tracking-wide text-[#6f4810]">Merged</p><p className="mt-1 text-xl font-black text-[#6f4810]">{counts?.merged ?? 0}</p></div>
            <div className="rounded border border-[#dc553c]/25 bg-[#dc553c]/5 p-3"><p className="text-[10px] font-bold uppercase tracking-wide text-[#8c2d1f]">Stopped safely</p><p className="mt-1 text-xl font-black text-[#8c2d1f]">{counts?.stopped_safe ?? 0}</p></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-[#d6bd7c]/60 pt-3 text-xs text-[#6d5b3f]">
            <span><strong className="text-[#2f2414]">Production skill:</strong> UGC Flowkit Production</span>
            <span><strong className="text-[#2f2414]">Planner:</strong> Codex → Gemini fallback</span>
            <span><strong className="text-[#2f2414]">Duration:</strong> {batch.config.durationSec}s</span>
            <span><strong className="text-[#2f2414]">Mode:</strong> {batch.config.mode === 'character' ? 'Family character' : 'Faceless'}</span>
            <span><strong className="text-[#2f2414]">Auto generate:</strong> {batch.config.autoGenerate ? 'on' : 'off'}</span>
            <span><strong className="text-[#2f2414]">Auto merge:</strong> {batch.config.autoMerge ? 'on' : 'off'}</span>
            <span><strong className="text-[#2f2414]">Product gallery:</strong> {batch.products.reduce((total, product) => total + (product.runtime?.productGallery?.length ?? (product.runtime?.productMediaId ? 1 : 0)), 0)} images registered</span>
            {batch.config.mode === 'character' && <span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5" /><strong className="text-[#2f2414]">Shared family:</strong> wife · husband · baby</span>}
          </div>
        </>}
      </section>

      {batch?.runtime?.blocked && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded border border-[#dc553c]/40 bg-[#dc553c]/10 px-4 py-3 text-xs text-[#8c2d1f]"><span><strong>Batch paused safely — {batch.runtime.blocked.code}:</strong> {batch.runtime.blocked.message}. No queued product will be attempted until the shared Flowkit upload issue is resolved.</span><button type="button" onClick={() => void resumeAfterBridgeRepair()} disabled={resuming} className="shrink-0 rounded border border-[#dc553c]/50 bg-[#fff8ec] px-3 py-1.5 text-[11px] font-black text-[#8c2d1f] hover:bg-[#dc553c]/10 disabled:opacity-60">{resuming ? 'Resuming…' : 'Resume after bridge repair'}</button></div>}
      {batch?.isQueued && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#d6bd7c] bg-[#fdf6e9] p-4 text-xs text-[#6d5b3f] shadow-sm">
          <div className="flex items-center gap-3">
            <Clock3 className="h-5 w-5 shrink-0 text-[#865916]" />
            <div>
              <p className="font-bold text-[#2f2414]">Batch đang chờ trong Hàng đợi (Vị trí #{batch.queuePosition || 1})</p>
              <p className="mt-0.5">Hệ thống đang chạy batch trước. Batch này sẽ tự động khởi động ngay khi batch trước hoàn thành.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWorkspaceTab('batches')}
            className="shrink-0 rounded border border-[#d6bd7c] bg-white px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50"
          >
            Xem Hàng đợi
          </button>
        </div>
      )}
      {/* Workspace navigation is ALWAYS rendered so users are never trapped on broken/missing batches */}
      <nav aria-label="UGC Flowkit workspace" className="flex overflow-x-auto sm:flex-wrap gap-2 rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-2 shadow-sm">
        {WORKSPACE_TABS.map(tab => {
          const Icon = tab.icon;
          const selected = tab.id === workspaceTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setWorkspaceTab(tab.id)}
              className={`inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-lg px-3 py-2 text-xs font-black transition-all ${
                selected ? 'bg-[#b54426] text-white shadow-sm' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {error && (
        <div role="alert" className="rounded-xl border border-[#dc553c]/40 bg-[#dc553c]/10 p-4 text-xs text-[#8c2d1f] shadow-sm space-y-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[#dc553c] mt-0.5" />
            <div>
              <p className="font-bold text-sm text-[#8c2d1f]">Không thể tải batch này</p>
              <p className="mt-1 text-xs text-[#8c2d1f]/90 leading-relaxed font-mono">{error}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1 border-t border-[#dc553c]/20">
            <button
              type="button"
              onClick={() => setWorkspaceTab('batches')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#865916] bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] shadow-sm"
            >
              <History className="h-3.5 w-3.5" />
              Xem Batches & Hàng đợi
            </button>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Thử tải lại
            </button>
            <button
              type="button"
              onClick={() => {
                setUgcFlowkitBatchId('');
                setWorkspaceTab('batches');
                setError('');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#dc553c]/40 bg-white px-3 py-1.5 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/10"
            >
              <X className="h-3.5 w-3.5" />
              Bỏ chọn batch này
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('masterPrompt')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#b54426] bg-[#b54426] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#8f3219]"
            >
              <Play className="h-3.5 w-3.5" />
              Quay lại UGC Master
            </button>
          </div>
        </div>
      )}

      {!batch && !error && (
        <div className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-8 text-center text-sm text-[#6d5b3f]">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#865916] mb-2" />
          Loading batch status…
        </div>
      )}

      {!batch && error && workspaceTab !== 'batches' && workspaceTab !== 'rules' && (
        <div className="rounded-xl border border-dashed border-[#d6bd7c] bg-[#fff8ec]/60 p-8 text-center text-xs text-[#6d5b3f]">
          <p className="font-bold text-[#2f2414] text-sm">Chưa có dữ liệu batch để hiển thị tab này</p>
          <p className="mt-1 text-[#6d5b3f]">Vui lòng chọn một batch khả dụng từ tab "Batches / Queue" ở phía trên hoặc tạo batch mới.</p>
          <button
            type="button"
            onClick={() => setWorkspaceTab('batches')}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#865916] px-4 py-2 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810]"
          >
            <History className="h-3.5 w-3.5" />
            Mở tab Batches & Hàng đợi
          </button>
        </div>
      )}

      {batch && (workspaceTab === 'references' || workspaceTab === 'plan') && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 text-xs shadow-sm">
        <h3 className="text-sm font-black text-[#2f2414]">Family voice assignments</h3>
        <p className="mt-1 text-[#6d5b3f]">Faceless keeps family portraits out of scene image inputs. The selected adult voice stays the same across both clips; Bé Mây may contribute one short audio-only reaction.</p>
        <div className="mt-3 grid gap-2 lg:grid-cols-2">{batch.products.filter(product => product.runtime?.voiceProfiles?.length || product.runtime?.plan?.voiceRole).map(product => {
          const profile = product.runtime?.voiceProfiles?.find(item => item.role === product.runtime?.plan?.voiceRole);
          const baby = product.runtime?.plan?.scenes.find(scene => scene.babyInterjection)?.babyInterjection;
          return <div key={`${product.id}-voice`} className="rounded border border-[#d6bd7c]/50 bg-white/50 p-2.5">
            <b className="text-[#2f2414]">{product.name}</b>
            <p className="mt-1 text-[#6d5b3f]">Lead VO: {profile?.name ?? (product.runtime?.plan?.voiceRole ? `${product.runtime.plan.voiceRole} (profile pending)` : 'Awaiting plan')}{baby ? ` · Bé Mây: ${baby}` : ''}</p>
            <p className="text-[#6d5b3f]">Visual cast: {batch.config.mode === 'faceless' ? 'none (product/location only)' : product.runtime?.plan?.castRoles.join(', ') || 'awaiting plan'}</p>
          </div>;
        })}</div>
      </section>}

      {batch && workspaceTab === 'references' && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm"><h3 className="text-sm font-black text-[#2f2414]">Reference registry</h3><p className="mt-1 text-xs text-[#6d5b3f]">Film-style reference ledger. Product image is uploaded once; every usable media ID is a Flowkit UUID, never a CAMS/base64 value.</p><div className="mt-3 rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] px-3 py-2 text-xs text-[#6d5b3f]"><strong className="text-[#2f2414]">{batch.config.mode === 'character' ? 'Family characters' : 'Faceless mode'}:</strong> {batch.config.mode === 'character' ? `reuse stored references from ${batch.config.familyCharacterFolder}; no portrait, appearance, or voice upload is requested.` : 'only product and location references are used.'}</div><div className="mt-3 grid gap-3 lg:grid-cols-2">{batch.products.map(product => <article key={product.id} className="rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs"><div className="flex items-start justify-between gap-2"><strong className="text-sm text-[#2f2414]">{product.name}</strong><span className="rounded border border-[#d6bd7c] px-2 py-0.5 text-[10px] font-bold text-[#6d5b3f]">{product.runtime?.phase ?? 'queued'}</span></div><div className="mt-3 grid gap-2"><ReferenceLine label="Flow project" id={product.runtime?.projectId} /><ReferenceLine label="Product visual asset UUID" id={product.runtime?.productMediaId} /><ReferenceLine label="Location request UUID" id={product.runtime?.locationRequestId} /><ReferenceLine label="Location media UUID" id={product.runtime?.locationMediaId} /></div>{product.runtime?.productUploadFile && <p className="mt-2 break-all text-[11px] text-[#6d5b3f]">Source file: {product.runtime.productUploadFile.split('/').at(-1)}</p>}{product.runtime?.phase === 'awaiting_bridge' && <p className="mt-2 text-[#8c2d1f]">Checkpoint retained. The product image will not be resent automatically.</p>}</article>)}</div></section>}

      {batch && workspaceTab === 'references' && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm"><h3 className="text-sm font-black text-[#2f2414]">Product gallery registry</h3><p className="mt-1 text-xs text-[#6d5b3f]">Every source image is registered once. The planner analyzes all images and dynamically selects the best visual reference for each scene based on culinary context and staging.</p><div className="mt-3 grid gap-3 lg:grid-cols-2">{batch.products.filter(product => product.runtime?.productGallery?.length).map(product => <article key={`${product.id}-gallery`} className="rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs"><strong className="text-sm text-[#2f2414]">{product.name}</strong><p className="mt-1 text-[#6d5b3f]">{product.runtime!.productGallery!.length} gallery images provisioned</p><ol className="mt-2 space-y-1">{product.runtime!.productGallery!.map((image, index) => <li key={image.filePath} className="flex items-center justify-between gap-3 rounded border border-[#d6bd7c]/40 bg-white/45 px-2 py-1"><span className="truncate">Image {index + 1} · {image.fileName}</span><code title={image.mediaId}>{image.mediaId ? idShort(image.mediaId) : image.entityId ? 'awaiting upload' : 'awaiting entity'}</code></li>)}</ol></article>)}</div></section>}

      {batch && workspaceTab === 'references' && batch.products.some(product => product.runtime?.productVision?.imageEvidence?.length) && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm"><h3 className="text-sm font-black text-[#2f2414]">Product vision evidence</h3><p className="mt-1 text-xs text-[#6d5b3f]">Every source image is inspected before the location reference and script. Visible food and packaging are separate evidence locks.</p><div className="mt-3 space-y-3">{batch.products.filter(product => product.runtime?.productVision?.imageEvidence?.length).map(product => <details key={`${product.id}-vision`} className="rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs"><summary className="cursor-pointer font-bold text-[#2f2414]">{product.name} · {product.runtime!.productVision!.sourceImageCount} inspected images · {product.runtime!.productVision!.servingContext}</summary><p className="mt-2 text-[#6d5b3f]">Location: {product.runtime!.productVision!.locationPrompt}</p><div className="mt-2 space-y-2">{product.runtime!.productVision!.imageEvidence!.map(item => <div key={item.sourceMediaId} className="rounded border border-[#d6bd7c]/40 bg-white/50 p-2"><p className="font-semibold">{item.fileName} · {idShort(item.sourceMediaId)}</p><p>Visible: {item.visibleProductDetails}</p><p>Package: {item.packagingAndLabelLock}</p><p>Physical contents: {item.insidePackagingProductLock}</p><p>Supported actions: {item.supportedVisualActions.join(', ') || 'none'}</p></div>)}</div></details>)}</div></section>}

      {batch && workspaceTab === 'references' && batch.config.mode === 'character' && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm"><h3 className="text-sm font-black text-[#2f2414]">Shared family UUIDs</h3><p className="mt-1 text-xs text-[#6d5b3f]">The same stored portraits and voice descriptions are reused across product videos; each scene includes only the roles selected by its plan.</p><div className="mt-3 grid gap-3 lg:grid-cols-2">{batch.products.filter(product => product.runtime?.familyReferences?.length).map(product => <article key={`${product.id}-family`} className="rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs"><strong className="text-[#2f2414]">{product.name}</strong><div className="mt-2 space-y-1">{product.runtime!.familyReferences!.map(reference => <ReferenceLine key={reference.role} label={`${reference.role} · ${reference.name}`} id={reference.mediaId} />)}</div></article>)}</div></section>}

      {batch && workspaceTab === 'plan' && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-black text-[#2f2414]">Production plans</h3>
            <p className="mt-1 text-xs text-[#6d5b3f]">Codex is default; Gemini gets the identical packaged skill only after verified quota/model unavailability. Each scene visibly separates `prompt` (frame 0) from `video_prompt` (motion).</p>
          </div>
          {batch.products.some(product => product.safeStop?.code === 'UGC_FLOWKIT_PLAN_INVALID') && <button type="button" onClick={() => void retryInvalidPlans()} disabled={retryingPlans} className="rounded bg-[#b54426] px-3 py-2 text-xs font-black text-white hover:bg-[#8f3219] disabled:opacity-60">{retryingPlans ? 'Retrying…' : 'Retry schema-invalid plans'}</button>}
        </div>

        {batch.config.promptOnly && (
          <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-[#865916]/40 bg-[#865916]/10 p-3 text-xs text-[#6f4810]">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#865916]" />
            <div>
              <p className="font-black text-[#2f2414]">Prompt Generate Only Mode Active</p>
              <p className="mt-0.5 text-[11px] leading-relaxed">Product images were uploaded to Google Flow. Flowkit did not send generation commands. Use the copy buttons below to copy Reference, Scene Image, and Video Motion prompts into Google Flow.</p>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-4">{batch.products.map(product => {
          const plan = product.runtime?.plan;
          const locationPrompt = product.runtime?.productVision?.locationPrompt || plan?.location?.name;
          return <article key={product.id} className="rounded-lg border border-[#d6bd7c]/70 bg-[#fdf6e9] p-3.5 text-xs shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#d6bd7c]/50 pb-2.5">
              <div>
                <strong className="text-sm text-[#2f2414]">{product.name}</strong>
                <p className="mt-0.5 text-[11px] text-[#6d5b3f]">{product.runtime?.planner ? `${product.runtime.planner} · skill ${product.runtime.skillSha256?.slice(0, 12) ?? 'recorded'}` : 'Awaiting provisioned references.'}{product.runtime?.productVision?.provider === 'gemini-fallback' ? ' · product vision: Gemini fallback' : ''}</p>
              </div>
              <div className="flex items-center gap-2">
                {product.runtime?.planningIssue && <span className="rounded border border-[#dc553c]/40 bg-[#dc553c]/10 px-2 py-0.5 font-bold text-[#8c2d1f]">{product.runtime.planningIssue.code}</span>}
                {plan && (
                  <button
                    type="button"
                    onClick={() => handleCopy(formatGoogleFlowPrompts(product), `all-${product.id}`)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#865916] bg-[#865916] px-2.5 py-1 text-[11px] font-bold text-[#fff8ec] hover:bg-[#6f4810] transition-colors"
                  >
                    {copiedId === `all-${product.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    <span>{copiedId === `all-${product.id}` ? 'Copied All Prompts!' : 'Copy All Google Flow Prompts'}</span>
                  </button>
                )}
              </div>
            </div>

            {plan ? <>
              {/* Reference Generation Prompt */}
              {locationPrompt && (
                <div className="mt-3 rounded border border-[#865916]/30 bg-white/70 p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-[#2f2414] flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-[#865916]" />
                      1. Reference Generation Prompt (Location)
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(locationPrompt, `loc-${product.id}`)}
                      className="inline-flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-[10px] font-bold text-[#865916] hover:bg-[#e9d6aa]/50"
                    >
                      {copiedId === `loc-${product.id}` ? <Check className="h-2.5 w-2.5 text-[#b87a14]" /> : <Copy className="h-2.5 w-2.5" />}
                      <span>{copiedId === `loc-${product.id}` ? 'Copied' : 'Copy Reference Prompt'}</span>
                    </button>
                  </div>
                  <p className="mt-1.5 rounded bg-[#fdf6e9]/80 p-1.5 text-[11px] text-[#2f2414] font-mono leading-relaxed select-all">{locationPrompt}</p>
                </div>
              )}

              <div className="mt-2.5 grid gap-2 md:grid-cols-2">
                <div className="rounded border border-[#d6bd7c]/40 bg-white/40 p-2"><b>Hook:</b> <span className="text-[#6d5b3f]">{plan.hook}</span></div>
                <div className="rounded border border-[#d6bd7c]/40 bg-white/40 p-2"><b>Concept:</b> <span className="text-[#6d5b3f]">{plan.concept}</span></div>
              </div>

              {/* Scene & Video Generation Prompts */}
              <div className="mt-3 space-y-2.5">
                {plan.scenes.map(scene => (
                  <div key={scene.number} className="rounded-lg border border-[#d6bd7c]/60 bg-white/65 p-3 shadow-xs">
                    <div className="flex items-center justify-between border-b border-[#d6bd7c]/40 pb-1.5">
                      <p className="font-black text-[#2f2414]">Scene {scene.number} (Duration: {scene.retainDurationSec}s · {scene.sourceReferenceName})</p>
                    </div>

                    {/* Frame 0 Still Prompt */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between">
                        <b className="text-[11px] text-[#2f2414]">2. Scene Frame-0 Still Prompt:</b>
                        <button
                          type="button"
                          onClick={() => handleCopy(scene.prompt, `scene-${product.id}-${scene.number}`)}
                          className="inline-flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-1.5 py-0.5 text-[10px] font-bold text-[#865916] hover:bg-[#e9d6aa]/50"
                        >
                          {copiedId === `scene-${product.id}-${scene.number}` ? <Check className="h-2.5 w-2.5 text-[#b87a14]" /> : <Copy className="h-2.5 w-2.5" />}
                          <span>{copiedId === `scene-${product.id}-${scene.number}` ? 'Copied' : 'Copy Scene Prompt'}</span>
                        </button>
                      </div>
                      <p className="mt-1 rounded bg-[#fdf6e9]/80 p-1.5 text-[11px] text-[#2f2414] font-mono leading-relaxed select-all">{scene.prompt}</p>
                    </div>

                    {/* 8s Video Motion Prompt */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between">
                        <b className="text-[11px] text-[#2f2414]">3. 8s Video Motion Prompt:</b>
                        <button
                          type="button"
                          onClick={() => handleCopy(scene.videoPrompt, `video-${product.id}-${scene.number}`)}
                          className="inline-flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-1.5 py-0.5 text-[10px] font-bold text-[#865916] hover:bg-[#e9d6aa]/50"
                        >
                          {copiedId === `video-${product.id}-${scene.number}` ? <Check className="h-2.5 w-2.5 text-[#b87a14]" /> : <Copy className="h-2.5 w-2.5" />}
                          <span>{copiedId === `video-${product.id}-${scene.number}` ? 'Copied' : 'Copy Video Prompt'}</span>
                        </button>
                      </div>
                      <p className="mt-1 rounded bg-[#fdf6e9]/80 p-1.5 text-[11px] text-[#2f2414] font-mono leading-relaxed select-all">{scene.videoPrompt}</p>
                    </div>

                    {/* Voiceover */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between">
                        <b className="text-[11px] text-[#2f2414]">Voiceover:</b>
                        <button
                          type="button"
                          onClick={() => handleCopy(scene.voiceover + (scene.babyInterjection ? ` (${scene.babyInterjection})` : ''), `vo-${product.id}-${scene.number}`)}
                          className="inline-flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-1.5 py-0.5 text-[10px] font-bold text-[#865916] hover:bg-[#e9d6aa]/50"
                        >
                          {copiedId === `vo-${product.id}-${scene.number}` ? <Check className="h-2.5 w-2.5 text-[#b87a14]" /> : <Copy className="h-2.5 w-2.5" />}
                          <span>{copiedId === `vo-${product.id}-${scene.number}` ? 'Copied' : 'Copy VO'}</span>
                        </button>
                      </div>
                      <p className="mt-1 text-[11px] text-[#6d5b3f] italic leading-relaxed">{scene.voiceover}{scene.babyInterjection ? ` (${scene.babyInterjection})` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </> : (
              <p className={`mt-3 ${product.runtime?.planningIssue ? 'text-[#8c2d1f]' : 'text-[#6d5b3f]'}`}>{product.runtime?.planningIssue?.message ?? product.safeStop?.message ?? 'No approved plan yet.'}</p>
            )}
          </article>;
        })}</div>
      </section>}

      {batch && workspaceTab === 'dashboard' && <section className="rounded-xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm"><h3 className="text-sm font-black text-[#2f2414]">UGC Flowkit project dashboard</h3><p className="mt-1 text-xs text-[#6d5b3f]">Dedicated UGC project ledger, separate from Film Character. Open the native dashboard for request-level Flowkit operations.</p><div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{batch.products.map(product => {
        const busy = productBusy[product.id];
        const canStop = ['queued', 'planning', 'validated', 'provisioned', 'generating'].includes(product.status);
        const canResume = product.status === 'stopped_safe';
        return <div key={product.id} className="rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs">
          <p className="truncate font-bold text-[#2f2414]">{product.name}</p>
          <p className="mt-1 text-[#6d5b3f]">Project: <code>{idShort(product.runtime?.projectId)}</code></p>
          <p className="text-[#6d5b3f]">Video: <code>{idShort(product.runtime?.videoId)}</code></p>
          <p className="text-[#6d5b3f]">Status: {STAGES[product.status].label}</p>
          <div className="mt-2.5 flex items-center gap-1.5 border-t border-[#d6bd7c]/40 pt-2">
            {canStop && <button type="button" onClick={() => void handleStopProduct(product.id)} disabled={Boolean(busy)} className="inline-flex items-center gap-1 rounded border border-[#dc553c]/50 bg-white/70 px-2 py-0.5 text-[10px] font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50"><Square className="h-2.5 w-2.5 fill-current" />{busy === 'stopping' ? 'Stopping…' : 'Stop'}</button>}
            {canResume && <button type="button" onClick={() => void handleResumeProduct(product.id)} disabled={Boolean(busy) || Boolean(batch?.runtime?.blocked)} className="inline-flex items-center gap-1 rounded border border-[#b54426]/50 bg-white/70 px-2 py-0.5 text-[10px] font-bold text-[#8f3219] hover:bg-[#b54426]/15 disabled:opacity-50"><Play className="h-2.5 w-2.5 fill-current" />{busy === 'resuming' ? 'Resuming…' : 'Resume'}</button>}
            <button type="button" onClick={() => void handleDeleteProduct(product.id, product.name)} disabled={Boolean(busy)} className="inline-flex items-center gap-1 rounded border border-[#dc553c]/35 bg-white/70 px-2 py-0.5 text-[10px] font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50"><Trash2 className="h-2.5 w-2.5" />{busy === 'deleting' ? 'Deleting…' : 'Delete'}</button>
          </div>
        </div>;
      })}</div><button type="button" onClick={() => setActiveTab('flowkitDashboard')} className="mt-4 inline-flex items-center gap-2 rounded bg-[#b54426] px-3 py-2 text-xs font-black text-white hover:bg-[#8f3219]"><LayoutDashboard className="h-3.5 w-3.5" />Open Flowkit Dashboard</button></section>}

      {batch && workspaceTab === 'process' && <section className="overflow-hidden rounded-xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm">
        <div className="flex items-center justify-between border-b border-[#d6bd7c]/60 px-4 py-3"><div><h3 className="text-sm font-black text-[#2f2414]">Product progress</h3><p className="mt-0.5 text-xs text-[#6d5b3f]">Updates every 5 seconds while this batch is active.</p></div><span className="text-[11px] text-[#6d5b3f]">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Not updated yet'}</span></div>
        <div className="divide-y divide-[#d6bd7c]/50">
          {batch.products.map(product => {
            const stage = STAGES[product.status];
            const { completedCount, activeIndex, failedIndex } = getPipelineStepState(product);
            const busy = productBusy[product.id];
            const canStop = ['queued', 'planning', 'validated', 'provisioned', 'generating'].includes(product.status);
            const canResume = product.status === 'stopped_safe';
            return <article key={product.id} className="px-4 py-3 sm:flex sm:items-center sm:justify-between sm:gap-5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-[#2f2414]">{product.name}</p>
                <p className="mt-0.5 text-xs text-[#6d5b3f]">{product.safeStop ? `${product.safeStop.code}: ${product.safeStop.message}` : getStageDetail(product)}</p>
                <div className="mt-2 flex flex-wrap gap-1">{PIPELINE_STEPS.map((step, index) => {
                  let stepClass = 'border-[#d6bd7c]/70 text-[#6d5b3f]';
                  if (index < completedCount) {
                    stepClass = 'border-[#b87a14]/45 bg-[#b87a14]/15 text-[#6f4810]';
                  } else if (product.status === 'stopped_safe' && index === failedIndex) {
                    stepClass = 'border-[#dc553c]/40 bg-[#dc553c]/10 text-[#8c2d1f] font-black';
                  } else if (index === activeIndex) {
                    stepClass = 'border-[#b54426]/50 bg-[#b54426]/10 text-[#8f3219] font-bold animate-pulse';
                  }
                  return <span key={step} className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${stepClass}`}>{step}</span>;
                })}</div>
                {product.runtime?.merged && <div className="mt-3 max-w-[240px]"><video controls preload="metadata" src={product.runtime.merged.url} className="w-full rounded border border-[#d6bd7c]" /><a href={product.runtime.merged.url} target="_blank" rel="noreferrer" className="mt-1 block text-[11px] font-bold text-[#8f3219] underline">Open merged video · {product.runtime.merged.clipCount} clips</a></div>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-0 sm:flex-col sm:items-end">
                <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${stage.className}`}>{statusIcon(product.status)}{stage.label}</span>
                <div className="flex items-center gap-1.5">
                  {canStop && <button type="button" onClick={() => void handleStopProduct(product.id)} disabled={Boolean(busy)} title="Stop this product pipeline" aria-label={`Stop ${product.name}`} className="inline-flex items-center gap-1 rounded border border-[#dc553c]/50 bg-white/70 px-2 py-1 text-[11px] font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50"><Square className="h-3 w-3 fill-current" />{busy === 'stopping' ? 'Stopping…' : 'Stop'}</button>}
                  {canResume && <button type="button" onClick={() => void handleResumeProduct(product.id)} disabled={Boolean(busy) || Boolean(batch?.runtime?.blocked)} title="Resume this product pipeline" aria-label={`Resume ${product.name}`} className="inline-flex items-center gap-1 rounded border border-[#b54426]/50 bg-white/70 px-2 py-1 text-[11px] font-bold text-[#8f3219] hover:bg-[#b54426]/15 disabled:opacity-50"><Play className="h-3 w-3 fill-current" />{busy === 'resuming' ? 'Resuming…' : 'Resume'}</button>}
                  <button type="button" onClick={() => void handleDeleteProduct(product.id, product.name)} disabled={Boolean(busy)} title="Delete this product from pipeline" aria-label={`Delete ${product.name}`} className="inline-flex items-center gap-1 rounded border border-[#dc553c]/35 bg-white/70 px-2 py-1 text-[11px] font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50"><Trash2 className="h-3 w-3" />{busy === 'deleting' ? 'Deleting…' : 'Delete'}</button>
                </div>
              </div>
            </article>;
          })}
        </div>
      </section>}

      {workspaceTab === 'rules' && <section className="rounded-xl border border-[#865916]/30 bg-[#fdf6e9] p-4 text-xs text-[#2f2414]">
        <div className="flex items-center gap-2 font-black text-[#8f3219]"><Wand2 className="h-4 w-4" />UGC Flowkit Production skill</div>
        <p className="mt-1.5 leading-5 text-[#6d5b3f]">This is the packaged skill received identically by Codex and its Gemini fallback. It adapts canonical Flowkit recipes—not a second Film pipeline: create project → upload product UUID once → generate references → scene images → videos → concat → monitor.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">Reference contract</strong><p className="mt-1 leading-5 text-[#6d5b3f]">Every stable product, location, or character uses an exact Flowkit UUID in `imageInputs`; `CAMS…` and planner-invented IDs are rejected.</p></div>
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">Action-only prompts</strong><p className="mt-1 leading-5 text-[#6d5b3f]">`prompt` is frame zero. It never repeats a character’s appearance; reference images maintain continuity.</p></div>
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">Canonical prompts & duration</strong><p className="mt-1 leading-5 text-[#6d5b3f]">`prompt` is the frame-zero still; `video_prompt` is always an 8-second Flow motion contract. Studio computes retained clip durations from the requested final length (for example 14s = 6s + 8s, 16s = 8s + 8s) and normalizes planner metadata before validation. Every source clip still uses 0–3s, 3–6s, 6–8s camera beats.</p></div>
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">No montage drift</strong><p className="mt-1 leading-5 text-[#6d5b3f]">One product reference, setup, lighting, props, and location per video. Only framing and camera movement may change; diverse concepts happen across videos.</p></div>
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">VO and family voices</strong><p className="mt-1 leading-5 text-[#6d5b3f]">Sensory Gen-Z Vietnamese VO must be evidence-safe. Stored family `voice_description` values (max 30 words) preserve selected character voices.</p></div>
          <div className="rounded border border-[#d6bd7c] bg-[#fff8ec] p-3"><strong className="text-[#2f2414]">TikTok safety</strong><p className="mt-1 leading-5 text-[#6d5b3f]">No invented claims; require commercial disclosure and AI label for realistic AI media. No real child/private-person likeness without permission, or public-figure endorsement.</p></div>
        </div>
        <p className="mt-3 leading-5 text-[#6d5b3f]">Transport failures use `/fk-doctor`: `extension_connected` alone is not signed-RPC readiness. A browser refresh can log `NO_INJECTION_RESULT` inside the extension; it is not classified as a UGC product failure when subsequent signed RPCs succeed. A real shared transport failure pauses once, preserves the operation checkpoint, and needs explicit repair—never a blind resend.</p>
      </section>}

      {workspaceTab === 'batches' && renderBatchesTab()}
    </div>
  </div>;
}
